import './App.css';
import SearchField from './SearchField';
import ResultCardList from './ResultCardList';
import PlaceholderSearch from './PlaceholderSearch';
import Footer from './Footer';
import Pagination from './Pagination';
import { createAuthHeaders, createJsonPostHeaders } from './httpUtils';

import React, { useState, useEffect } from 'react';
import { useAuth } from "react-oidc-context";
import { handleCognitoResponse } from './authService.ts';
import Sidebar from './Sidebar';

function isAuthLoading(auth) {
  return auth.activeNavigator === "signinRedirect" || auth.isLoading;
}

function extractSuggestionItems(responseJson) {
  if (!responseJson || typeof responseJson !== 'object') {
    throw new Error('Invalid suggestions response format');
  }

  if (!Array.isArray(responseJson.suggestions)) {
    throw new Error('Invalid suggestions response format');
  }

  return responseJson.suggestions
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean);
}

function App() {
  const [input, setInput] = useState('');
  const [data, setData] = useState([]);
  const [query, setQuery] = useState('');
  const [originalQuery, setOriginalQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const standardSelectedFields = ["broadcast_series_title", "title", "teaser_text", "keywords"];
  const [selectedFields, setSelectedFields] = useState(standardSelectedFields);
  const [fieldBoosts, setFieldBoosts] = useState({ title: 2, keywords: 1.5 });
  const boostValues = selectedFields.map((field) => fieldBoosts[field] ?? 1);
  const [docTypeBoosts, setDocTypeBoosts] = useState([
    { value: 'channel', boost: 1.01 },
    { value: 'subchannel', boost: 1.01 },
    { value: 'broadcastseries', boost: 1.035 },
  ]);
  const activeDocTypeBoosts = docTypeBoosts.filter(
    (item) => item.value.trim() && item.boost !== '' && !Number.isNaN(Number(item.boost))
  );
  const docTypeBoostPayload = activeDocTypeBoosts.map((item) => ({
    term: item.value.trim(),
    boost: Number(item.boost),
  }));
  const [filters, setFilters] = useState([]);
  const transformedFilters = filters
    .filter(f => f.key && f.value)
    .map(f => ({ [f.key]: f.value }));
  const [notFilters, setNotFilters] = useState([]);
  const transformedNotFilters = notFilters
    .filter(f => f.key && f.value)
    .map(f => ({ [f.key]: f.value }));

  const [localWeight] = useState(0.3);

  const [intentSuggestions, setIntentSuggestions] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);



  const auth = useAuth();
  useEffect(() => {
    if (isAuthLoading(auth)) {
      // Wait for the redirect to complete
      return;
    }
    if (!isAuthLoading(auth)) {
      if (auth.isAuthenticated) {
        setIsModalOpen(false);
      } else {
        // Handle the Cognito response after redirect
        handleCognitoResponse()
          .then((isAuthenticated) => {
            if (isAuthenticated) {
              setIsModalOpen(false);
            } else {
              setIsModalOpen(true);
            }
          })
          .catch((err) => {
            console.error("Error handling Cognito response:", err);
            setIsModalOpen(true);
          });
      }
    }
  }, [auth, auth.isLoading, auth.isAuthenticated, auth.activeNavigator]);

  const handleLogin = () => {
    const CLIENT_ID = config.CLIENT_ID;
    const COGNITO_DOMAIN = config.COGNITO_DOMAIN;
    const redirectUri = `${window.location.origin}`;

    const loginUrl = `${COGNITO_DOMAIN}/login?client_id=${CLIENT_ID}&response_type=code&scope=email+openid+phone&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = loginUrl;
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sliderValue, setSliderValue] = useState(7000);
  const [maxResults, setMaxResults] = useState(10);
  const [fromPos, setFromPos] = useState(0);

  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetch('/config.json')
      .then((res) => {
        if (!res.ok) {
          throw new Error("config.json not found or not accessible");
        }
        return res.json();
      })
      .then((json) => {
        if (!json.AUGMENT_ENDPOINT ||
          !json.CLIENT_ID ||
          !json.COGNITO_DOMAIN ||
          !json.HTTP_ACCEPT ||
          !json.SUGGESTION_ENDPOINT ||
          !json.PREFIX_SEARCH_ENDPOINT) {
          throw new Error("Missing required config values in config.json");
        }
        setConfig(json);
      })
      .catch((err) => {
        console.error('❌ Fehler beim Laden der Konfiguration:', err);
        setError("Die Anwendung konnte nicht gestartet werden – Konfiguration fehlt oder ist ungültig.");
      });
  }, []);

  const SEARCH_ENDPOINT = config?.AUGMENT_ENDPOINT;
  const SEARCH_SUGGESTIONS_ENDPOINT = config?.SUGGESTION_ENDPOINT;
  const PREFIX_SEARCH_ENDPOINT = config?.PREFIX_SEARCH_ENDPOINT;

  useEffect(() => {
    if (!config) {
      return;
    }

    const trimmedInput = input.trim();

    if (trimmedInput.length < 3) {
      setSearchSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        setSuggestionsLoading(true);

        const response = await fetch(SEARCH_SUGGESTIONS_ENDPOINT, {
          method: 'POST',
          headers: createJsonPostHeaders(config),
          body: JSON.stringify({
            query: trimmedInput,
            relevant_fields: selectedFields,
            num_of_suggestions: 5,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Suggestions failed with status ${response.status}`);
        }

        const responseJson = await response.json();
        const normalizedSuggestions = extractSuggestionItems(responseJson)
          .filter((suggestion) => suggestion.toLowerCase() !== trimmedInput.toLowerCase())
          .slice(0, 5);

        setSearchSuggestions(normalizedSuggestions);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch suggestions:', err);
          setSearchSuggestions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setSuggestionsLoading(false);
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [config, input, selectedFields, SEARCH_SUGGESTIONS_ENDPOINT]);

  if (error) {
    return (
      <div className="fatal-error">
        <h2>❌ Fehler beim Laden der Anwendung</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!config) return <p>Lade Konfiguration...</p>;


  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSliderChange = (event) => {
    const value = event.target.value;
    setSliderValue(value);
  };

  const handleMaxResultsChange = (event) => {
    setMaxResults(Number(event.target.value));
  };

  const handleReset = () => {
    setData([]);
    setOriginalQuery('');
    setQuery('');
    setInput('');
    setError(null);
    setFromPos(0);
    setIntentSuggestions([]);
    setSearchSuggestions([]);
  };

  const handleSearchWithCorrection = async (fromPosition = fromPos) => {
    handleSearch({
      endpoint: SEARCH_ENDPOINT,
      body: JSON.stringify({
        "input": {
          "query": input,
          "threshold": (sliderValue * 0.0001),
          "max_results": maxResults,
          "from_pos": fromPosition,
          "search_fields": selectedFields,
          "field_boosts": boostValues,
          "doc_type_boosts": docTypeBoostPayload,
          "filter_key_value": transformedFilters,
          "not_filter_key_value": transformedNotFilters
        }
      })
    });
  };

  const handleSimpleSearch = async (fromPosition = fromPos) => {
    handleSearch({
      endpoint: SEARCH_ENDPOINT,
      body: JSON.stringify({
        "input": {
          "query": input,
          "threshold": (sliderValue * 0.0001),
          "max_results": maxResults,
          "from_pos": fromPosition,
          "search_fields": selectedFields,
          "field_boosts": boostValues,
          "doc_type_boosts": docTypeBoostPayload,
          "filter_key_value": transformedFilters,
          "not_filter_key_value": transformedNotFilters,
          "correct_query": false
        }
      })
    });
  };

  // Wrapper functions for new searches that reset fromPos
  const handleNewAugmentSearch = async () => {
    setFromPos(0);
    await handleSearchWithCorrection(0);
    await handleIntentSearch(input);
  };

  const handleNewBasicSearch = async () => {
    setFromPos(0);
    await handleSimpleSearch(0);
    await handleIntentSearch(input);
  };

  // Pagination handlers
  const handlePreviousPage = async () => {
    const newFromPos = Math.max(0, fromPos - maxResults);
    setFromPos(newFromPos);
    // Use the last search type (augment is default)
    if (originalQuery && originalQuery !== query) {
      await handleSimpleSearch(newFromPos);
    } else {
      await handleSearchWithCorrection(newFromPos);
    }
  };

  const handleNextPage = async () => {
    const newFromPos = fromPos + maxResults;
    setFromPos(newFromPos);
    // Use the last search type (augment is default)
    if (originalQuery && originalQuery !== query) {
      await handleSimpleSearch(newFromPos);
    } else {
      await handleSearchWithCorrection(newFromPos);
    }
  };

  const handleSearch = async ({ endpoint, body }) => {
    if (!endpoint) {
      console.error("Kein Endpoint definiert – Abbruch.");
      setError("Konfiguration fehlerhaft. Kein Endpoint definiert.");
      return;
    }

    setError(null);
    try {
      setIsLoading(true);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, endpoint: ${endpoint}, response: ${errorText}`);
      }

      const responseJson = await response.json();

      setData(responseJson.output.documents);
      setOriginalQuery(responseJson.output.original_query);
      setQuery(responseJson.output.query);

      setIsLoading(false);
    } catch (err) {
      console.error('Failed to fetch error: ' + err.message);
      console.error("Failed to fetch: ", err);
      setError('Failed to fetch error: ' + err.message);
      setIsLoading(false);
    }
  };

  const handleAutocompleteSelect = async (suggestion) => {
    setSearchSuggestions([]);
    setInput(suggestion);
    setQuery(suggestion);
    await handleNewAugmentSearchForQuery(suggestion);
  };

  const handleNewAugmentSearchForQuery = async (nextQuery) => {
    setFromPos(0);
    setInput(nextQuery);

    await handleSearch({
      endpoint: SEARCH_ENDPOINT,
      body: JSON.stringify({
        input: {
          query: nextQuery,
          threshold: sliderValue * 0.0001,
          max_results: maxResults,
          from_pos: 0,
          search_fields: selectedFields,
          field_boosts: boostValues,
          doc_type_boosts: docTypeBoostPayload,
          filter_key_value: transformedFilters,
          not_filter_key_value: transformedNotFilters
        }
      })
    });

    await handleIntentSearch(nextQuery);
  };

  // HINT: prefix search itself only returns IDs, not metadata
  //       therefore, a separate metadata endpoint must be specified for each item /metadata/{item_id}
  //       keep this in mind when configuring the number of search results
  const handlePrefixSearch = async (letter) => {
    setFromPos(0);
    setInput(letter);

    setError(null);
    try {
      setIsLoading(true);

      const response = await fetch(PREFIX_SEARCH_ENDPOINT, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify({
          query: letter,
          suggestion_field: "title",
          num_of_suggestions: maxResults,
          return_ids: true
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, endpoint: ${PREFIX_SEARCH_ENDPOINT}, response: ${errorText}`);
      }

      const responseJson = await response.json();
      const suggestions = responseJson.suggestions || [];

      const metadataPromises = suggestions.map(async (suggestion) => {
        try {
          const metaResponse = await fetch(`/api/metadata/${suggestion.external_id}`, {
            method: 'GET',
            headers: createAuthHeaders(config),
          });
          if (!metaResponse.ok) {
            return null;
          }
          return await metaResponse.json();
        } catch {
          return null;
        }
      });

      const metadataResults = await Promise.all(metadataPromises);
      const documents = metadataResults
        .filter(Boolean)
        .map((meta) => {
          const data = meta.gcp_metadata || meta.opensearch_metadata || {};
          const titleParts = (data.title || '').split(',');
          return {
            title: titleParts[0]?.trim() || '',
            broadcast_series_title: '',
            teaser_image_url: data.teaser_image_url || '/stock-img.png',
            live_url: data.live_url || null,
            teaser_text: data.teaser_text || ''
          };
        });

      setData(documents);
      setQuery(letter);
      setOriginalQuery(letter);
    } catch (err) {
      console.error('Failed to fetch error: ' + err.message);
      setError('Failed to fetch error: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIntentSearch = async (query) => {
    if (!query) {
      setIntentSuggestions([])
      return;
    }


    try {
      const response = await fetch('api/search/intent/invoke', {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify({
          input: {
            query: query,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Intent search failed');
      }

      const json = await response.json();
      setIntentSuggestions(json);
    } catch (err) {
      console.error('Failed to fetch error: ' + err.message);
      console.error("Failed to fetch: ", err);
    }
  };

  if (isAuthLoading(auth)) {
    return <div>Loading...</div>;
  }

  return (
    <div className="App">
      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h2>Please Log In</h2>
            <button onClick={handleLogin}>Log In</button>
          </div>
        </div>
      )}
      {!isModalOpen && (
        <>
          <Sidebar
            isOpen={isSidebarOpen}
            sliderValue={sliderValue}
            onSliderChange={handleSliderChange}
            maxResults={maxResults}
            onMaxResultsChange={handleMaxResultsChange}
            selectedFields={selectedFields}
            setSelectedFields={setSelectedFields}
            fieldBoosts={fieldBoosts}
            setFieldBoosts={setFieldBoosts}
            docTypeBoosts={docTypeBoosts}
            setDocTypeBoosts={setDocTypeBoosts}
            filters={filters}
            setFilters={setFilters}
            notFilters={notFilters}
            setNotFilters={setNotFilters}
            localWeight={localWeight}
          />

          <button
            className="toggle-btn"
            onClick={toggleSidebar}
            aria-label="Sidebar umschalten"
          >
            {isSidebarOpen ? '←' : '→'}
          </button>

          <header className="App-header">
            <SearchField
              input={input}
              onInputChange={setInput}
              onSendClick={handleNewAugmentSearch}
              suggestions={searchSuggestions}
              suggestionsLoading={suggestionsLoading}
              onSuggestionSelect={handleAutocompleteSelect}
            />

            {
              isLoading ?
                <div className="spinner"></div>
                : error ?
                  <span className='App-content'>
                    <div className='error-msg'></div>
                    {error.includes('400') ? 'Bitte formuliere deine Suchanfrage um' : 'Oops - etwas ist schief gegangen'}
                    <div className="reset-btn" onClick={handleReset}>reset</div>
                  </span>
                  : <div className='App-content'>
                    {originalQuery !== query && <div onClick={handleNewBasicSearch} className="basic-search-query">Meintest Du doch: {originalQuery}? </div>}
                    {query ?
                      <>
                        <ResultCardList
                          title={query}
                          items={data}
                          originalQuery={originalQuery}
                          intentSuggestions={intentSuggestions}
                          onSuggestionClick={async (suggestion) => {
                            setIsLoading(true);
                            setInput(suggestion);
                            setQuery(suggestion);
                            setFromPos(0);
                            await handleIntentSearch(suggestion);
                            await handleSearch({
                              endpoint: SEARCH_ENDPOINT,
                              body: JSON.stringify({
                                input: {
                                  query: suggestion,
                                  threshold: sliderValue * 0.0001,
                                  max_results: maxResults,
                                  from_pos: 0,
                                  search_fields: selectedFields,
                                  field_boosts: boostValues,
                                  filter_key_value: transformedFilters,
                                  not_filter_key_value: transformedNotFilters,
                                  doc_type_boosts: docTypeBoostPayload
                                }
                              })
                            });
                          }}
                        />
                        <Pagination
                          fromPos={fromPos}
                          maxResults={maxResults}
                          onPrevious={handlePreviousPage}
                          onNext={handleNextPage}
                          hasResults={data.length}
                        />
                      </>
                      : <PlaceholderSearch onLetterClick={handlePrefixSearch} />
                    }
                    {query ? (<div className="reset-btn" onClick={handleReset}>reset</div>) : null}
                  </div>
            }
          </header>
          <Footer />
        </>
      )}
    </div>
  );
}

export default App;
